import { useEffect, useState } from 'react';

import { HorizontalContainer, VerticalContainer, Wrapper } from '@american-autonomy/ag-ui';

import Modal from 'components/Modal';
import GhostButton from 'components/buttons/GhostButton';
import SecondaryButton from 'components/buttons/SecondaryButton';

import LabeledToggle from 'pages/ApplicationDetailsPage/components/FlightPlannerPanel/components/LabeledToggle';

import { CONFIRM_BUTTON_TEST_ID, TEST_ID } from './constants';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const RateColumnPicker: FunctionComponent<Props> = ({
    cancelText,
    className = '',
    columns,
    confirmText,
    hint,
    isOpen,
    onCancel,
    onColumnSelected,
    testId = TEST_ID,
    title
}) => {
    const [selectedColumn, setSelectedColumn] = useState<string | null>(null);

    // Reset the local picker selection whenever the modal opens or the column
    // set changes. Without this, a stale pick from a prior upload would leak
    // into the next session.
    useEffect(() => {
        if (isOpen) {
            setSelectedColumn(columns[0] ?? null);
        }
    }, [columns, isOpen]);

    const options = columns.map(column => ({ label: column, value: column }));
    const activeIndex = selectedColumn ? columns.indexOf(selectedColumn) : -1;

    const handleToggleClick = (value: string) => {
        setSelectedColumn(value);
    };

    const handleConfirmClick = () => {
        if (!selectedColumn) {
            return;
        }

        onColumnSelected({ rateColumn: selectedColumn });
    };

    const footer = (
        <HorizontalContainer className={styles.actions}>
            <GhostButton onClick={onCancel} text={cancelText} />

            <SecondaryButton
                isDisabled={!selectedColumn}
                onClick={handleConfirmClick}
                testId={CONFIRM_BUTTON_TEST_ID}
                text={confirmText}
            />
        </HorizontalContainer>
    );

    return (
        <Modal
            className={`${className} ${styles.rateColumnPicker}`}
            footer={footer}
            isOpen={isOpen}
            onClose={onCancel}
            size='small'
            testId={testId}
            title={title}>
            <VerticalContainer>
                <Wrapper className={styles.hint}>{hint}</Wrapper>

                <LabeledToggle
                    activeIndex={activeIndex}
                    label={title}
                    onClick={handleToggleClick}
                    options={options}
                />
            </VerticalContainer>
        </Modal>
    );
};

export default RateColumnPicker;
